import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../hooks/useSubscription';
import TrialBanner from '../subscription/TrialBanner';
import { 
  Network, LayoutDashboard, Building2, Sparkles, FileText, Settings, LogOut,
  ChevronRight, ShieldCheck, Zap, GitBranch, CreditCard, Users, Shield, Activity, Code, Book, TrendingUp, ShieldAlert, Award, BarChart2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import CartIcon from '../cart/CartIcon';
import BackButton from './BackButton';
import OliviaPanel from '../olivia/OliviaPanel';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/entities', label: 'Entities', icon: Building2 },
  { path: '/structure', label: 'Structure View', icon: GitBranch },
  { path: '/ai-analysis', label: 'AI Analysis', icon: Sparkles },
  { path: '/fundability', label: 'Fundability', icon: TrendingUp },
  { path: '/ip-registry', label: 'IP Registry', icon: Award },
  { path: '/trademark', label: 'Trademark Filing', icon: FileText },
  { path: '/investor-diligence', label: 'Investor DD', icon: BarChart2 },
  { path: '/audit-certification', label: 'Audit & Certify', icon: ShieldCheck },
  { path: '/compliance', label: 'Compliance', icon: Activity },
  { path: '/ucc', label: 'UCC Filings', icon: Shield },
  { path: '/pricing', label: 'Services', icon: CreditCard },
  { path: '/trust-center', label: 'Trust Center', icon: ShieldAlert },
  { path: '/teams', label: 'Teams', icon: Users },
  { path: '/rule-builder', label: 'Rule Builder', icon: Zap },
  { path: '/audit-log', label: 'Activity Log', icon: FileText },
];

export default function DashboardLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { isTrial, daysRemaining } = useSubscription();
  const [showTrialBanner, setShowTrialBanner] = useState(true);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const shouldShowBanner = isTrial && showTrialBanner;

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col" data-testid="dashboard-layout">
      {/* Trial Banner */}
      {shouldShowBanner && (
        <TrialBanner 
          daysRemaining={daysRemaining} 
          onClose={() => setShowTrialBanner(false)}
        />
      )}
      
      <div className="flex flex-1">
      {/* Sidebar */}
      <aside className="w-64 border-r border-slate-800 bg-slate-950/95 backdrop-blur flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-slate-800">
          <Link to="/dashboard" className="flex items-center gap-3">
            <img 
              src="/corpcraft-logo.png" 
              alt="CorpCraft Logo" 
              className="h-10 w-auto object-contain"
            />
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
                {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
              </Link>
            );
          })}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-4 px-2">
            <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-slate-300 font-medium">
              {user?.full_name?.charAt(0) || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{user?.full_name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-400 hover:text-red-400 hover:bg-red-500/10"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Top bar with back + cart */}
        <div className="sticky top-0 z-30 flex justify-between items-center px-8 py-3 bg-slate-950/80 backdrop-blur border-b border-slate-800/60">
          <BackButton />
          <CartIcon />
        </div>
        <div className="p-8">
          {children}
        </div>
      </main>
      </div>

      {/* Global Olivia AI guide — fixed bottom-right on every page */}
      <OliviaPanel />
    </div>
  );
}
